import { Button, Form, Input, message, Radio } from "antd";
import { useGetData } from "../../../../Network/useGetQueryData";
import { SearchIcon } from "../../../../Icons/SearchIcon";
import { required } from "../../../../utils/form-validations";
import "./style.scss";
import { Loading } from "../../../../components/Loading";
import { usePostData } from "../../../../Network/usePostData";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { groupByProvider } from "../../../../utils/groupProvidersData";
import { useTranslation } from "react-i18next";

export const ProviderStep = ({
  onChangeStep,
  formData,
  setFormData,
}: {
  onChangeStep?: (step: number) => void;
  formData?: any;
  setFormData?: any;
}) => {
  const { t, i18n } = useTranslation();
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [goNext, setGoNext] = useState<boolean>();
  const [providersData, setProviders] = useState<any>();
  const [btnLoading, setBtnLoading] = useState<boolean>();

  const [form] = Form.useForm();

  const { data: serviceProviders, isLoading } = useGetData<{
    data: any;
  }>({
    queryKey: ["service-provider-users"],
    url: `service-provider-services?serviceIds=${formData?.servicesIds.join(",")}&offset=0&limit=10&withDeleted=false`,
    options: {
      enabled: !!formData?.servicesIds,
      queryKey: ["service-provider-users", formData?.servicesIds],
    },
  });

  const { mutateAsync } = usePostData({
    url: "service-request",
    options: {
      onSuccess: () => {
        if (goNext) {
          onChangeStep?.(3);
        } else navigate("/requests");
      },
      onError: (error) => {
        //@ts-ignore
        message.error(error?.response?.data?.message);
      },
    },
  });

  const { mutateAsync: calculatePrice } = usePostData({
    url: "service-request/calculate-prices",
    options: {
      onSuccess: () => {},
      onError: (error) => {
        //@ts-ignore
        message.error(error?.response?.data?.message);
      },
    },
  });

  const handleCalculatePrice = async () => {
    const filteredData = serviceProviders?.data?.items?.map((item: any) => {
      return {
        serviceProviderId: item?.serviceProviderId,
        serviceId: item?.serviceId,
      };
    });

    const response = await calculatePrice({
      services: filteredData,
      customerId: formData?.customerId,
    });
    //@ts-ignore
    setProviders(response?.data);
  };

  useEffect(() => {
    if (!serviceProviders) return;
    handleCalculatePrice();
  }, [serviceProviders]);

  const { mutateAsync: addServices } = usePostData({
    url: "service-request/services",
    options: {
      onSuccess: () => {
        if (goNext) {
          onChangeStep?.(3);
        } else navigate("/requests");
      },
      onError: (error) => {
        //@ts-ignore
        message.error(error?.response?.data?.message);
      },
    },
  });

  const handleAddRequest = async () => {
    setBtnLoading(true);
    const values = {
      services: formData?.summary?.map((ser: any) => {
        return {
          serviceId: ser?.id,
          serviceProviderId: ser?.serviceProvider?.serviceProvider?.id,
        };
      }),
      paymentMethod: "cash",
      customerId: formData?.customerId,
      requestedBy: formData?.requestedBy || "admin",
      type: "withServices",
    };
    try {
      if (requestId)
        addServices({
          services: formData?.summary?.map((ser: any) => {
            return {
              serviceId: ser?.id,
              serviceProviderId: ser?.serviceProvider?.serviceProvider?.id,
            };
          }),
          serviceRequestId: +requestId,
        });
      else await mutateAsync(values);
      setBtnLoading(false);
    } catch (error) {
      setBtnLoading(false);
    }
  };

  const ProviderCard = ({ info }: { info: any }) => (
    <div className="service-provider-card">
      <div className="title-logo">
        <span>
          <img
            src={info?.serviceProvider?.logoUrl || "logo"}
            alt="logo-url"
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "/assets/providers/mokhtabar.png";
            }}
          />
        </span>
        <div>
          <p>
            {info?.serviceProvider?.name?.[i18n.language || "en"] ||
              t("shared.provider")}
          </p>
          <span>
            {info?.services?.length === formData?.servicesIds?.length
              ? t("requests.allIncluded")
              : t("requests.includedServices", {
                  services: info?.services
                    .map(
                      (service: any) => service?.name?.[i18n.language || "en"],
                    )
                    .join(", "),
                })}
          </span>
        </div>
      </div>
      <div className="service-provider-prices">
        <div className="share-price">
          <p>{t("requests.medhubShare")}</p>
          <span>{info?.totalMedHubPrice?.toFixed(2)} </span>
        </div>
        <div className="discount-price">
          <p>{t("requests.youPay")}</p>
          <span>{info?.totalPrice?.toFixed(2)} </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Form form={form} layout="vertical" onFinish={() => {}}>
        <div className="request-provider-service-list">
          <div className="form-list-header">
            <p>{t("requests.providers")}</p>

            <div className="header-search">
              <Input
                size="large"
                placeholder={t("shared.searchUsers")}
                prefix={<SearchIcon />}
              />
            </div>
          </div>
          {providersData?.services?.length > 0 ? (
            <Form.Item rules={[required]} name="providers">
              <Radio.Group value={0} className="step-providers-wrapper">
                {groupByProvider(providersData?.services)?.map((pro) =>
                  pro?.serviceProvider?.id || pro?.serviceProviderId ? (
                    <Radio
                      key={pro?.serviceProviderId || pro?.serviceProvider?.id}
                      value={pro?.serviceProviderId || pro?.serviceProvider?.id}
                      onChange={(e) => {
                        if (e.target?.checked) {
                          setFormData({
                            ...formData,
                            summary: pro?.services?.map((ser: any) => {
                              return {
                                ...ser,
                                serviceProvider: pro,
                              };
                            }),
                          });
                        }
                      }}
                    >
                      <ProviderCard info={pro} />
                    </Radio>
                  ) : null,
                )}
              </Radio.Group>
            </Form.Item>
          ) : isLoading || !providersData ? (
            <Loading />
          ) : (
            <p
              style={{
                height: "200px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {t("requests.noProviders")}
            </p>
          )}
        </div>
      </Form>

      <div className="steps-action-footer">
        <Button onClick={() => onChangeStep?.(1)}>
          {t("shared.previous")}
        </Button>
        <Button
          type="link"
          style={{ margin: "0 8px" }}
          onClick={async () => {
            try {
              const values = await form.validateFields();
              setFormData({
                ...formData,
                ...values,
                serviceProviderId: values,
              });
              await handleAddRequest();
            } catch (error) {}
          }}
        >
          {t("shared.saveAndClose")}
        </Button>

        <Button
          type="primary"
          onClick={async () => {
            try {
              const values = await form.validateFields();
              setFormData({
                ...formData,
                ...values,
                serviceProviderId: values,
              });
              onChangeStep?.(3);
            } catch (error) {}
          }}
          loading={btnLoading}
        >
          {t("shared.next")}
        </Button>
      </div>
    </>
  );
};
